import React from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Subject } from '../../types/subject';
import styles from '../../subject.module.css';
import catalogStyles from '../../../../layouts/Catalog/Catalog.module.css';

export const SUBJECT_ICON_MAP: Record<string, string> = {
    "amazing-people": "group",
    "environmental-studies": "eco",
    "financial-literacy": "account_balance",
    "science": "science",
    "technology-and-engineering": "memory",
    "wildlife": "pets",
    "health-wellness": "shield_with_heart",
    "career": "work"
};

interface SubjectCardProps {
    subject: Subject;
    isStudent: boolean;
    isSuperAdmin: boolean;
    progress?: { attempted: number; avgScore: number };
    totalModules?: number;
    showOnlyImageAndName?: boolean;
    linkTo?: string;
    openMenuId?: string | null;
    setOpenMenuId?: (id: string | null) => void;
    menuRef?: React.RefObject<HTMLDivElement | null>;
}

const SubjectCard: React.FC<SubjectCardProps> = ({
    subject,
    isStudent,
    isSuperAdmin,
    progress = { attempted: 0, avgScore: 0 },
    totalModules = 0,
    showOnlyImageAndName = false,
    linkTo,
    openMenuId,
    setOpenMenuId,
    menuRef
}) => {
    const navigate = useNavigate();
    const resolvedLink = linkTo || `/subject/${subject.slug}`;

    const renderThumbnail = (className: string) => {
        if (subject.thumbnail_url) {
            return (
                <img
                    src={subject.thumbnail_url}
                    alt={subject.name}
                    className={className}
                />
            );
        }

        return (
            <div className={className} style={{
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af'
            }}>
                <span className="material-symbols-outlined" style={{ fontSize: '4rem' }}>
                    {SUBJECT_ICON_MAP[subject.slug] || 'book_5'}
                </span>
            </div>
        );
    };

    if (showOnlyImageAndName) {
        return (
            <Link to={resolvedLink} className={styles.studentCard + " " + catalogStyles.catalogCard} style={{
                padding: '1rem',
                background: 'transparent',
                borderColor: 'gray'
            }}>
                <div style={{
                    height: 80,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'white'
                }}>
                    <span className="material-symbols-outlined">
                        {SUBJECT_ICON_MAP[subject.slug] || 'book_5'}
                    </span>
                </div>
                <div className={styles.studentCardContent} style={{
                    paddingTop: 0,
                    paddingBottom: 0
                }}>
                    <h2 className={styles.studentCardName} style={{ fontWeight: 600, color: 'white' }}>{subject.name}</h2>
                </div>
            </Link>
        );
    }

    if (isStudent) {
        return (
            <div className={styles.studentCard}>
                {renderThumbnail(styles.studentCardThumbnail)}
                <div className={styles.studentCardContent}>
                    <h2 className={styles.studentCardName}>{subject.name}</h2>

                    <div className={styles.studentProgressInfo}>
                        <div className={styles.modulesLine}>
                            {progress.attempted} <span>/</span> {totalModules}
                        </div>
                        <div className={styles.attemptedLabel}>Modules Attempted</div>
                        <div className={styles.avgLabel}>Avg: {progress.avgScore}%</div>
                    </div>

                    <Link
                        to={resolvedLink}
                        className={styles.button}
                    >
                        Continue Learning
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.card}>
            <div className={styles.thumbnailContainer}>
                {renderThumbnail(styles.thumbnail)}
                {isSuperAdmin && setOpenMenuId && (
                    <div className={styles.cardMenu} ref={openMenuId === subject.id ? menuRef : null}>
                        <button
                            className={styles.menuToggleButton}
                            onClick={(e) => {
                                e.preventDefault();
                                setOpenMenuId(openMenuId === subject.id ? null : subject.id);
                            }}
                        >
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                        {openMenuId === subject.id && (
                            <div className={styles.menuDropdown}>
                                <button
                                    className={styles.dropdownOption}
                                    onClick={() => navigate(`/subject/edit/${subject.slug}`)}
                                >
                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className={styles.cardContent}>
                <h2 className={styles.cardName}>{subject.name}</h2>
                {/* <p className={styles.cardModuleCount}>{totalModules} Modules available</p> */}
                <div className={styles.cardActions}>
                    <Link
                        to={resolvedLink}
                        className={styles.button}
                    >
                        Explore
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SubjectCard;
